import { Component, Input, forwardRef, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  UntypedFormControl,
  ValidationErrors,
  AbstractControl
} from '@angular/forms';
import { Logger } from '@core/services';

import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import defaultLanguageModel from '@zxcvbn-ts/language-en';

const log = new Logger('PasswordFieldComponent');

@Component({
  selector: 'app-password-field',
  templateUrl: './password-field.component.html',
  styleUrls: ['./password-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PasswordFieldComponent),
      multi: true
    }
  ]
})
export class PasswordFieldComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() label = 'Password';
  @Input() placeholder = '';
  @Input() inputId = 'password';
  @Input() confirm = false;
  @Input() validateCriteria = true;
  @Input() minStrength = 3;
  @Input() confirmLabel = 'Confirm Password';
  @Input() confirmPlaceholder = 'Confirm password';
  @Input() containerClass = '';
  @Input() labelClass = '';
  @Input() inputClass = '';
  @Input() toggleButtonClass = '';
  @Input() confirmInputClass = '';
  @Input() confirmToggleButtonClass = '';
  @Input() strengthBarClass = '';
  @Input() suggestionsClass = '';
  @Input() formControl: AbstractControl | null = null;

  value = '';
  confirmValue = '';
  touched = false;
  confirmTouched = false;

  showPassword = false;
  showConfirm = false;

  strength = 0;
  strengthLabel = 'Weak';
  strengthClass = 'text-danger';
  strengthPercent = 10;

  criteria = {
    minLength: false,
    upperLower: false,
    number: false,
    special: false
  };

  get passwordValue(): string {
    return this.value;
  }

  get control(): UntypedFormControl | null {
    return this.formControl instanceof UntypedFormControl ? this.formControl : null;
  }

  ngOnInit(): void {
    const { dictionary, translations } = defaultLanguageModel;
    zxcvbnOptions.setOptions({ dictionary, translations });

    if (this.value) {
      this.markMainTouched();
    }
  }

  writeValue(val: string): void {
    this.value = val || '';
    log.debug('[writeValue] Password value set:', { hasValue: Boolean(this.value) });
    this.evaluateStrength(this.value);
  }

  setValue(val: string): void {
    this.value = val;
    log.debug('[setValue] Password updated:', { hasValue: Boolean(this.value) });
    this.onChange(val);
    this.markMainTouched();
    this.evaluateStrength(val);
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirm(): void {
    this.showConfirm = !this.showConfirm;
  }

  markMainTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  onConfirmInput(val: string): void {
    this.confirmValue = val;
    log.debug('[onConfirmInput] Confirm value updated:', {
      hasValue: Boolean(this.confirmValue)
    });
    this.markConfirmTouched();
    this.onChange(this.confirmValue);
  }

  markConfirmTouched(): void {
    this.confirmTouched = true;
    log.debug('[markConfirmTouched] Confirm field touched');
    this.onChange(this.value); // force re-validation
    this.onTouched();
  }

  evaluateStrength(password: string): void {
    const result = zxcvbn(password);
    this.strength = result.score;

    log.debug('[evaluateStrength] Score:', result.score);
    log.debug('[evaluateStrength] Feedback:', result.feedback);

    const scores = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
    const classes = ['text-danger', 'text-warning', 'text-secondary', 'text-info', 'text-success'];
    const percents = [10, 25, 50, 75, 100];

    this.strengthLabel = scores[result.score];
    this.strengthClass = classes[result.score];
    this.strengthPercent = percents[result.score];

    this.criteria.minLength = password.length >= 8;
    this.criteria.upperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
    this.criteria.number = /\d/.test(password);
    this.criteria.special = /[^\w\s]/.test(password);

    log.debug('[evaluateStrength] Criteria:', this.criteria);
  }

  validate(_: UntypedFormControl): ValidationErrors | null {
    const errors: ValidationErrors = {};

    if (!this.value?.trim()) {
      errors['required'] = true;
      log.debug('[validate] Password is empty.');
      return errors;
    }

    if (!this.validateCriteria) {
      if (this.confirm && this.value !== this.confirmValue) {
        errors['passwordMismatch'] = true;
      }
      log.debug('[validate] Criteria bypassed. Errors:', errors);
      return Object.keys(errors).length > 0 ? errors : null;
    }

    const hasMinLength = this.value.length >= 8;
    const hasUpperLower = /[a-z]/.test(this.value) && /[A-Z]/.test(this.value);
    const hasNumber = /\d/.test(this.value);
    const hasSpecial = /[^\w\s]/.test(this.value);

    if (!hasMinLength) {
      errors['minLength'] = true;
    }

    if (!hasUpperLower) {
      errors['upperLower'] = true;
    }

    if (!hasNumber) {
      errors['number'] = true;
    }

    if (!hasSpecial) {
      errors['special'] = true;
    }

    if (this.confirm && this.value !== this.confirmValue) {
      errors['passwordMismatch'] = true;
    }

    log.debug('[validate] Final validation errors:', errors);
    return Object.keys(errors).length > 0 ? errors : null;
  }

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};
}
